import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';

import Text from '../../Atoms/Text';
import { ProgressiveDiscount } from '../../../shared';
import './index.sass';

const messages = defineMessages({
	incentiveAveragePercent: {
		id: 'progressiveIncentive.incentiveAveragePercent',
		defaultMessage: 'Levando mais {count}, cada foto sai {percent}% mais barata!',
	},
	incentiveRangePercent: {
		id: 'progressiveIncentive.incentiveRangePercent',
		defaultMessage: 'Levando mais 1, ela sairá {percent}% mais barata!',
	},
	incentivePrice: {
		id: 'progressiveIncentive.incentivePrice',
		defaultMessage: 'Levando mais {count}, cada foto sai por {price}!',
	},
	lastTier: {
		id: 'progressiveIncentive.lastTier',
		defaultMessage: 'Leve a {total}ª foto por apenas mais {cost}!',
	},
});

const formatCurrency = (value) => {
	return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
};

class IncentiveToast extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			isVisible: false,
			isExiting: false,
			snapshotData: null,
		};
		this.hideTimer = null;
		this.exitTimer = null;
	}

	componentDidUpdate(prevProps) {
		if (this.props.trigger !== prevProps.trigger && this.props.trigger > 0) {
			this.showToast();
		}
	}

	componentWillUnmount() {
		if (this.hideTimer) clearTimeout(this.hideTimer);
		if (this.exitTimer) clearTimeout(this.exitTimer);
	}

	showToast = () => {
		if (this.hideTimer) clearTimeout(this.hideTimer);
		if (this.exitTimer) clearTimeout(this.exitTimer);

		const { rangesPrice, currentPhotoCount, maxAvailablePhotos } = this.props;
		const snapshotData = ProgressiveDiscount.getIncentiveData(rangesPrice, currentPhotoCount, maxAvailablePhotos);

		if (!snapshotData) {
			this.setState({ isVisible: false, isExiting: false, snapshotData: null });
			return;
		}

		this.setState({ isVisible: true, isExiting: false, snapshotData });

		this.hideTimer = setTimeout(() => {
			this.setState({ isExiting: true });

			this.exitTimer = setTimeout(() => {
				this.setState({ isVisible: false, isExiting: false, snapshotData: null });
			}, 400);
		}, 4000);
	};

	renderMessage(data) {
		const { intl } = this.props;
		const { formatMessage } = intl;

		if (data.type === 'lastTier') {
			return formatMessage(messages.lastTier, {
				cost: formatCurrency(data.additionalCost),
				total: data.totalPhotos,
			});
		}

		if (data.averageSavingsPercent >= 50) {
			return formatMessage(messages.incentiveAveragePercent, {
				count: data.photosNeeded,
				percent: data.averageSavingsPercent,
			});
		}

		if (data.rangeSavingsPercent >= 50 && data.photosNeeded === 1) {
			return formatMessage(messages.incentiveRangePercent, {
				percent: data.rangeSavingsPercent,
			});
		}

		return formatMessage(messages.incentivePrice, {
			count: data.photosNeeded,
			price: formatCurrency(data.averagePrice),
		});
	}

	getHighlightValue(data) {
		if (data.type === 'lastTier') {
			return formatCurrency(data.additionalCost);
		}
		if (data.averageSavingsPercent >= 50) {
			return `${data.averageSavingsPercent}%`;
		}
		if (data.rangeSavingsPercent >= 50 && data.photosNeeded === 1) {
			return `${data.rangeSavingsPercent}%`;
		}
		return formatCurrency(data.averagePrice);
	}

	render() {
		const { isVisible, isExiting, snapshotData } = this.state;

		if (!isVisible || !snapshotData) return null;

		const data = snapshotData;

		const message = this.renderMessage(data);
		const highlight = this.getHighlightValue(data);

		if (!message) return null;

		const parts = message.split(highlight);
		const hasHighlight = parts.length > 1;

		const className = `IncentiveToast ${isExiting ? 'IncentiveToast--exiting' : 'IncentiveToast--entering'}`;

		const isModal = this.props.isModalOpen;

		const toastStyle = {
			top: isModal ? '80px' : '130px',
			background: isModal
				? 'linear-gradient(135deg, rgba(245, 124, 0, 0.78) 0%, rgba(230, 74, 25, 0.78) 100%)'
				: 'linear-gradient(135deg, rgba(245, 124, 0, 0.95) 0%, rgba(230, 74, 25, 0.95) 100%)',
			border: '1px solid rgba(255, 235, 180, 0.4)',
			backdropFilter: 'blur(6px)',
			opacity: isModal ? 0.85 : 1,
		};

		return (
			<div className={className} style={toastStyle}>
				<Text className="IncentiveToast__message">
					{hasHighlight ? (
						<>
							{parts[0]}
							<span className="IncentiveToast__highlight">
								{highlight}
							</span>
							{parts[1]}
						</>
					) : (
						message
					)}
				</Text>
			</div>
		);
	}
}

IncentiveToast.propTypes = {
	trigger: PropTypes.number,
	isModalOpen: PropTypes.bool,
	rangesPrice: PropTypes.arrayOf(
		PropTypes.shape({
			min_photos: PropTypes.number,
			max_photos: PropTypes.number,
			price: PropTypes.number,
		})
	),
	currentPhotoCount: PropTypes.number,
	maxAvailablePhotos: PropTypes.number,
	intl: PropTypes.shape().isRequired,
};

IncentiveToast.defaultProps = {
	trigger: 0,
	rangesPrice: [],
	currentPhotoCount: 0,
	maxAvailablePhotos: Infinity,
	isModalOpen: false,
};

export default injectIntl(IncentiveToast);
